/**
 * Fetch wrapper for the public services API. No session: every route this
 * site calls is public (intake, the status-token page, badge verification).
 *
 * Relative `/api` by default — the Vite proxy in dev, Firebase Hosting's
 * rewrite on the test site. Production sets VITE_API_ORIGIN to the load
 * balancer (https://api.floto.ai), as apps/web does.
 */
export const apiOrigin: string =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Nest answers `{ message: string | string[] }`; surface it, not the status. */
async function messageOf(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { message?: unknown } | null;
    const message = body?.message;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length > 0) {
      return message.map(String).join(', ');
    }
  } catch {
    // Not JSON — fall through.
  }
  return fallback;
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  // FormData sets its own multipart boundary; a JSON content type would break it.
  const isForm = init?.body instanceof FormData;
  const res = await fetch(`${apiOrigin}/api${path}`, {
    ...init,
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    throw new ApiError(
      await messageOf(res, 'Something went wrong. Please try again.'),
      res.status,
    );
  }

  return (await res.json()) as T;
}
